import api from './api';

/**
 * Service for prayer times API calls
 */
const prayerTimesService = {
    /**
     * Get prayer times by coordinates
     * @param {Object} params - Query parameters
     * @returns {Promise} - API response
     */
    getTimingsByCoordinates: async (params) => {
        try {
            const response = await api.get('/timings', { params });
            return response.data;
        } catch (error) {
            console.error('Error fetching prayer times by coordinates:', error);
            throw error;
        }
    },

    /**
     * Get prayer times by address
     * @param {string} address - Address string
     * @param {Object} params - Additional query parameters
     * @returns {Promise} - API response
     */
    getTimingsByAddress: async (params) => {
        try {
            const response = await api.get('/timingsByAddress', { params });
            return response.data;
        } catch (error) {
            console.error('Error fetching prayer times by address:', error);
            throw error;
        }
    },

    /**
     * Get prayer times for next prayer
     * @param {Object} params - Query parameters
     * @returns {Promise} - API response
     */
    getNextPrayer: async (params) => {
        try {
            const response = await api.get('/nextPrayer', { params });
            return response.data;
        } catch (error) {
            console.error('Error fetching next prayer:', error);
            throw error;
        }
    },

    /**
     * Get calendar for a month
     * @param {number} year - Year
     * @param {number} month - Month (1-12)
     * @param {Object} params - Additional query parameters
     * @returns {Promise} - API response
     */
    getCalendar: async (year, month, params = {}) => {
        try {
            // Try the new monthly-timings endpoint first
            try {
                const response = await api.get(`/monthly-timings/${year}/${month}`, { params });
                return response.data;
            } catch (newApiError) {
                // Fall back to the old API if the new one fails
                console.log('New API endpoint failed, trying legacy endpoint:', newApiError.message);
                const response = await api.get(`/calendar/${year}/${month}`, { params });
                return response.data;
            }
        } catch (error) {
            console.error('Error fetching calendar:', error);
            throw error;
        }
    },

    /**
     * Get monthly prayer times
     * @param {number} year - Year
     * @param {number} month - Month (1-12)
     * @param {Object} params - Additional query parameters
     * @returns {Promise} - API response
     */
    getMonthlyPrayerTimes: async (year, month, params = {}) => {
        try {
            console.log(`Fetching monthly prayer times for ${year}/${month}`);
            const response = await api.get(`/monthly-timings/${year}/${month}`, { params });
            return response.data;
        } catch (error) {
            console.error('Error fetching monthly prayer times:', error);
            throw error;
        }
    },

    /**
     * Get yearly prayer times
     * @param {number} year - Year
     * @param {Object} params - Additional query parameters
     * @returns {Promise} - API response
     */
    getYearlyPrayerTimes: async (year, params = {}) => {
        try {
            console.log(`Fetching yearly prayer times for ${year}`);

            // Always use the simplified yearly endpoint
            const response = await api.get(`/simple-yearly-timings/${year}`, { params });
            return response.data;
        } catch (error) {
            console.error('Error fetching yearly prayer times:', error);
            throw error;
        }
    },

    /**
     * Get monthly prayer times by address
     * @param {number} year - Year
     * @param {number} month - Month (1-12)
     * @param {Object} params - Additional query parameters including address
     * @returns {Promise} - API response
     */
    getMonthlyPrayerTimesByAddress: async (year, month, params = {}) => {
        try {
            console.log(`Fetching monthly prayer times by address for ${year}/${month}`);
            const response = await api.get(`/monthly-timingsByAddress/${year}/${month}`, { params });
            return response.data;
        } catch (error) {
            console.error('Error fetching monthly prayer times by address:', error);
            throw error;
        }
    },

    /**
     * Get yearly prayer times by address
     * @param {number} year - Year
     * @param {Object} params - Additional query parameters including address
     * @returns {Promise} - API response
     */
    getYearlyPrayerTimesByAddress: async (year, params = {}) => {
        try {
            console.log(`Fetching yearly prayer times by address for ${year}`);
            const response = await api.get(`/yearly-timingsByAddress/${year}`, { params });
            return response.data;
        } catch (error) {
            console.error('Error fetching yearly prayer times by address:', error);
            throw error;
        }
    },

    /**
     * Convert Gregorian date to Hijri
     * @param {string} date - Date in YYYY-MM-DD format
     * @param {Object} params - Additional query parameters
     * @returns {Promise} - API response
     */
    gregorianToHijri: async (date, params = {}) => {
        try {
            const response = await api.get(`/gToH/${date}`, { params });
            return response.data;
        } catch (error) {
            console.error('Error converting date to Hijri:', error);
            throw error;
        }
    },

    /**
     * Convert Hijri date to Gregorian
     * @param {string} date - Date in YYYY-MM-DD format
     * @param {Object} params - Additional query parameters
     * @returns {Promise} - API response
     */
    hijriToGregorian: async (date, params = {}) => {
        try {
            const response = await api.get(`/hToG/${date}`, { params });
            return response.data;
        } catch (error) {
            console.error('Error converting Hijri to Gregorian:', error);
            throw error;
        }
    },

    /**
     * Get special Islamic dates/holidays
     * @param {number} year - Hijri year
     * @param {Object} params - Additional query parameters
     * @returns {Promise} - API response
     */
    getHijriHolidays: async (year, params = {}) => {
        try {
            const response = await api.get(`/hijriHolidays/${year}`, { params });
            return response.data;
        } catch (error) {
            console.error('Error fetching Hijri holidays:', error);
            throw error;
        }
    },

    /**
     * Get prayer times calculation methods
     * @returns {Promise} - API response with available calculation methods
     */
    getMethods: async () => {
        try {
            const response = await api.get('/methods');
            return response.data;
        } catch (error) {
            console.error('Error fetching calculation methods:', error);
            throw error;
        }
    },

    /**
     * Get available juristic schools
     * @returns {Promise} - API response with available juristic schools
     */
    getSchools: async () => {
        try {
            const response = await api.get('/schools');
            return response.data;
        } catch (error) {
            console.error('Error fetching juristic schools:', error);
            throw error;
        }
    },

    /**
     * Get current day's Hijri date
     * @param {Object} params - Additional query parameters
     * @returns {Promise} - API response
     */
    getCurrentHijriDate: async (params = {}) => {
        try {
            const response = await api.get('/hijri', { params });
            return response.data;
        } catch (error) {
            console.error('Error fetching current Hijri date:', error);
            throw error;
        }
    }
};

export default prayerTimesService;